import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../Common/Loading';

// --- Interfaces ---
interface QuestionStat {
    perc: number;
    total: number;
    display_text: string;
}

interface GridRow {
    lesson_code: string;
    lesson_name: string;
    avg_score: number;
    questions: Record<string, QuestionStat>;
}

const EL_Questionperformance: React.FC = () => {
    const API_BASE = process.env.REACT_APP_DOCUMENT_LINK || 'https://staging.sysadmin.elearnoncloud.com/';
    const navigate = useNavigate();

    // --- State Management ---
    const [country, setCountry] = useState('Australia');
    const [dics, setDics] = useState<string[]>([]);
    const [selectedDic, setSelectedDic] = useState('--Select--');
    const [threshold, setThreshold] = useState(70);
    const [famWise, setFamWise] = useState(true);

    // Date Mode & Filters
    const [dateMode, setDateMode] = useState<'All' | 'Datewise'>('Datewise');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const [gridData, setGridData] = useState<GridRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const [selectedRowCode, setSelectedRowCode] = useState<string | null>(null);

    const MAX_QUESTIONS = 100;
    const columns = Array.from({ length: MAX_QUESTIONS }, (_, i) => String(i + 1).padStart(3, '0'));

    const colors = {
        headBg: '#c7ddfd',
        headText: '#2859ac',
        border: '#80a0ff',
        rowOdd: '#f4faff',
        rowEven: '#fff',
        rowSelected: 'orange',
        failBg: 'red',
        failText: 'black'
    };

    // --- Styles ---
    const styles = {
        body: { fontFamily: 'Arial', fontSize: '13pt', margin: 0, overflowX: 'hidden' as const },
        titleBar: { backgroundColor: 'teal', color: 'white', textAlign: 'center' as const, fontSize: '16pt', padding: '10px' },
        filterBar: { padding: '10px', display: 'flex', alignItems: 'center', flexWrap: 'wrap' as const, gap: '10px' },
        select: { fontFamily: 'Arial', fontSize: '14pt', color: 'blue', padding: '2px' },
        inputDate: { fontFamily: 'Arial', fontSize: '12pt', padding: '2px 5px', color: 'blue' },
        btn: { fontFamily: 'Arial', fontSize: '12pt', cursor: 'pointer', padding: '2px 10px' },
        closeBtn: { fontFamily: 'Arial', fontSize: '14pt', cursor: 'pointer', padding: '4px 15px', marginLeft: 'auto' },
        gridContainer: { overflowX: 'auto' as const, overflowY: 'auto' as const, maxHeight: '700px', width: '100%', marginTop: '10px', borderTop: `0px solid ${colors.border}`, borderBottom: `2px solid ${colors.border}` },

        table: { borderCollapse: 'collapse' as const, width: 'max-content', fontSize: '13px', fontFamily: 'Arial' },
        th: { backgroundColor: colors.headBg, color: colors.headText, border: `1px solid ${colors.border}`, padding: '6px', textAlign: 'center' as const, position: 'sticky' as const, top: -1, zIndex: 2 },
        thSub: { backgroundColor: colors.headBg, color: colors.headText, border: `1px solid ${colors.border}`, padding: '6px', textAlign: 'center' as const, position: 'sticky' as const, top: '29px', zIndex: 2 },
        td: { borderRight: `1px solid #a3c0e8`, padding: '10px', textAlign: 'center' as const, whiteSpace: 'nowrap' as const },
        radioLabel: { fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }
    };

    useEffect(() => {
        document.body.style.backgroundColor = "#fff";
    }, []);

    // --- Effects ---
    useEffect(() => {
        const fetchDics = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}api/q_perf_get_dics?country=${country}`);
                const json = await res.json();
                setDics(json.dics || []);
                setSelectedDic('--Select--');
                setGridData([]);
                setHasSearched(false);
            } catch (err) {
                console.error("Error fetching DICs", err);
            } finally {
                setLoading(false);
            }
        };
        fetchDics();
    }, [country]);

    // --- Handlers ---
    const handleGo = async () => {
        if (selectedDic === '--Select--') {
            alert("Please select a Dicfile.");
            return;
        }

        if (dateMode === 'Datewise' && (!fromDate || !toDate)) {
            alert("Please select both From Date and To Date.");
            return;
        }

        setLoading(true);
        setHasSearched(true);
        setSelectedRowCode(null);

        try {
            const res = await fetch(`${API_BASE}api/q_perf_get_data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    country,
                    dic_code: selectedDic,
                    date_mode: dateMode,
                    from_date: fromDate,
                    to_date: toDate,
                    fam_wise: famWise
                })
            });
            const json = await res.json();

            if (json.error) {
                alert(json.error);
            } else {
                setGridData(json.results || []);
            }
        } catch (err) {
            console.error("Error fetching performance data", err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        window.location.href = "/SysAdmin";
    };

    // --- Cleaned up Routing Logic ---
    const handleQuestionClick = (e: React.MouseEvent, lessonCode: string, qnNo: string) => {
        e.stopPropagation();

        let url = '';
        if (famWise) {
            // If family-wise is checked, go to QuestionInsights
            const params = new URLSearchParams({
                Title: lessonCode,
                DICCode: selectedDic,
                Qnno: qnNo
            });

            if (dateMode === 'Datewise') {
                params.append('Fromlsndate', fromDate);
                params.append('Tolsndate', toDate);
            }

            url = `/SUPPORT/EL_QuestionInsights?${params.toString()}`;
        } else {
            // Minimal URL exactly as requested
            url = `/LessonBrowser/QuestionViewer?QnNo=${qnNo}&Title=${lessonCode}&Country=${country}&from=Questionperformance`;
        }

        window.open(url, '_blank');
    };

    const isDatewise = dateMode === 'Datewise';

    return (
        <div style={styles.body}>
            {loading && <Loading />}

            <div style={styles.titleBar}>
                QUESTION PERFORMANCE ANALYSIS
            </div>

            <div style={styles.filterBar}>
                <span>Country :</span>
                <select style={styles.select} value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="Australia">Australia</option>
                    <option value="Canada">Canada</option>
                </select>

                <span>Dicfile :</span>
                <select style={{ ...styles.select, width: '200px' }} value={selectedDic} onChange={(e) => setSelectedDic(e.target.value)}>
                    <option>--Select--</option>
                    {dics.map(d => <option key={d} value={d}>{d}</option>)}
                </select>

                {/* Radio Buttons for All / Datewise */}
                <div style={{ display: 'flex', gap: '10px', marginLeft: '5px', borderLeft: '2px solid #ccc', paddingLeft: '10px' }}>
                    <label style={styles.radioLabel}>
                        <input type="radio" value="All" checked={dateMode === 'All'} onChange={() => setDateMode('All')} /> All
                    </label>
                    <label style={styles.radioLabel}>
                        <input type="radio" value="Datewise" checked={dateMode === 'Datewise'} onChange={() => setDateMode('Datewise')} /> Datewise
                    </label>
                </div>

                {/* Date Pickers (Disabled if "All" is selected) */}
                <span style={{ opacity: isDatewise ? 1 : 0.5 }}>From:</span>
                <input
                    type="date"
                    style={{ ...styles.inputDate, opacity: isDatewise ? 1 : 0.5 }}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={!isDatewise}
                />

                <span style={{ opacity: isDatewise ? 1 : 0.5 }}>To:</span>
                <input
                    type="date"
                    style={{ ...styles.inputDate, opacity: isDatewise ? 1 : 0.5 }}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={!isDatewise}
                />

                <select style={{ ...styles.select, height: '28px', marginLeft: '10px' }} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}>
                    {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10].map(v => (
                        <option key={v} value={v}>{v}</option>
                    ))}
                </select>

                <button style={styles.btn} onClick={handleGo}>GO</button>

                <label style={{ marginLeft: '10px', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <input type="checkbox" checked={famWise} onChange={(e) => setFamWise(e.target.checked)} style={{ marginRight: '5px' }} />
                    Family-wise Wrong Answers
                </label>

                <button style={styles.closeBtn} onClick={handleClose}>CLOSE</button>
            </div>
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                    textAlign: "center",
                    fontSize: "10pt",
                    padding: "10px",
                    color: "rgb(40, 89, 172)",
                    backgroundColor: "rgb(199, 221, 253)",
                    border: "1px solid rgb(128, 160, 255)",
                    fontWeight: "bold",

                }}
            >
                Question Nos And Marks
            </div>

            <div style={{ ...styles.gridContainer, marginTop: "0" }}>

                {hasSearched && gridData.length === 0 && !loading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>No data available for this dictionary.</div>
                ) : (
                    <table style={styles.table}>

                        <thead>
                            <tr>
                                <th style={{ ...styles.th, minWidth: '30px' }}>#</th>
                                <th style={{ ...styles.th, textAlign: 'left', minWidth: '100px' }}>LessonCode</th>
                                <th style={{ ...styles.th, textAlign: 'left', minWidth: '200px' }}>LessonName</th>
                                <th style={{ ...styles.th, minWidth: '80px' }}>Av lsnScore</th>

                                {columns.map(col => (
                                    <th key={col} style={{ ...styles.th, minWidth: '50px' }}>
                                        {parseInt(col)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {gridData.map((row, idx) => {
                                const isSelected = selectedRowCode === row.lesson_code;
                                const baseBg = idx % 2 === 0 ? colors.rowEven : colors.rowOdd;
                                const rowBg = isSelected ? colors.rowSelected : baseBg;

                                return (
                                    <tr
                                        key={row.lesson_code}
                                        style={{ backgroundColor: rowBg, cursor: 'pointer' }}
                                        onClick={() => setSelectedRowCode(row.lesson_code)}
                                    >
                                        <td style={styles.td}>{idx + 1}</td>
                                        <td style={{ ...styles.td, textAlign: 'left' }}>{row.lesson_code}</td>
                                        <td style={{ ...styles.td, textAlign: 'left' }}>{row.lesson_name}</td>
                                        <td
                                            style={{
                                                ...styles.td,
                                                backgroundColor: row.avg_score < threshold ? colors.failBg : 'transparent',
                                                color: '#000',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {row.avg_score}
                                        </td>

                                        {columns.map(col => {
                                            const qnStat = row.questions[col];
                                            let cellBg = 'transparent';
                                            let cellColor = '#000';

                                            if (qnStat && qnStat.perc < threshold) {
                                                cellBg = colors.failBg;
                                                cellColor = colors.failText;
                                            }

                                            return (
                                                <td
                                                    key={col}
                                                    style={{
                                                        ...styles.td,
                                                        backgroundColor: cellBg,
                                                        color: qnStat ? cellColor : 'transparent'
                                                    }}
                                                    onClick={(e) => {
                                                        if (qnStat) handleQuestionClick(e, row.lesson_code, col);
                                                    }}
                                                >
                                                    {qnStat ? (
                                                        <span style={{ color: cellColor, cursor: 'pointer' }}>
                                                            {qnStat.display_text}
                                                        </span>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default EL_Questionperformance;